import { UpdateType } from '@ardoq/api-types';
import { DoqType } from '@ardoq/doq';
import { ModalSize, confirm, info, modal } from '@ardoq/modal';
import { ResponseLogModal as ResponseLogModalBase } from './SurveyOverview/ResponseLog/ResponseLogModal';
import {
  PendingAnswersInvalidationState,
  PendingAnswersActionForEditedSurvey,
} from 'surveyAdmin/types';
import { Trans, t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const confirmUnsubscribeModal = async (surveyName: string) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeTitle',
      'Unsubscribe from survey digest'
    ),
    modalSize: ModalSize.S,
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeConfirmButton',
      'Yes'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeCancelButton',
      'No'
    ),
    doqType: DoqType.BROADCAST,
    text: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeText',
      'Do you want to unsubscribe?'
    ),
    text2: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeText2',
      { surveyName },
      'This means you will no longer receive the survey digest email from "{{surveyName}}". Are you sure you want to do this?'
    ),
  });
};

export const unsubscriptionSuccessModal = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders info() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return info({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeTitle',
      'Unsubscribe from survey digest'
    ),
    modalSize: ModalSize.S,
    primaryButtonText: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeSuccessPrimaryButton',
      'Close'
    ),
    doqType: DoqType.BROADCAST,
    text: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeSuccessText',
      'You have successfully unsubscribed.'
    ),
    text2: t(
      i18nLocale,
      'surveyAdmin:modals.unsubscribeSuccessText2',
      'If you want to subscribe again, go to the survey details section of the survey and modify the audience list.'
    ),
  });
};

export const confirmDescriptionChangeModal = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.overwriteDescriptionTitle',
      'Overwrite question description'
    ),
    modalSize: ModalSize.S,
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.overwriteDescriptionConfirmButton',
      'Yes'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.overwriteDescriptionCancelButton',
      'No, maintain my description'
    ),
    subtitle: t(
      i18nLocale,
      'surveyAdmin:modals.overwriteDescriptionSubtitle',
      'Clicking yes will overwrite your existing description.'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'surveyAdmin:modals.overwriteDescriptionWarning',
      "Would you like to overwrite the existing description with the new field's description?"
    ),
  });
};

export const confirmFieldChangeModal = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.fieldChangeTitle',
      'Field change warning'
    ),
    modalSize: ModalSize.XS,
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.fieldChangeConfirmButton',
      'Yes'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.fieldChangeCancelButton',
      'Cancel'
    ),
    subtitle: t(
      i18nLocale,
      'surveyAdmin:modals.fieldChangeSubtitle',
      'Do you still want to change this field?'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'surveyAdmin:modals.fieldChangeWarning',
      'There are some extra questions depending on this field question. After changing this field, your existing conditional questions will be removed.'
    ),
  });
};

export const confirmTurnOffChangeApproval = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.turnOffApprovalTitle',
      'Turn off response approval'
    ),
    modalSize: ModalSize.XS,
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.turnOffApprovalConfirmButton',
      'Confirm'
    ),
    isConfirmButtonDanger: true,
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.turnOffApprovalCancelButton',
      'Cancel'
    ),
    subtitle: t(
      i18nLocale,
      'surveyAdmin:modals.turnOffApprovalSubtitle',
      'Do you wish to continue?'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'surveyAdmin:modals.turnOffApprovalWarning',
      'After turning off this feature, all pending answers will be removed. Deletion is irreversible.'
    ),
  });
};

const getPendingAnswersWarningBlockMessage = (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
): string => {
  if (
    pendingAnswersInvalidationState.action ===
    PendingAnswersActionForEditedSurvey.WILL_DELETE
  ) {
    const numPendingAnswers = pendingAnswersInvalidationState.numPendingAnswers;
    if (numPendingAnswers)
      return `After persisting this change, ${numPendingAnswers} pending answer${numPendingAnswers === 1 ? '' : 's'} will be removed. Deletion is irreversible.`;
  } else if (
    pendingAnswersInvalidationState.action ===
    PendingAnswersActionForEditedSurvey.FAILED_TO_FETCH
  ) {
    return 'Could not confirm whether there are pending answers. If any exist, they will be removed. Deletion is irreversible.';
  }
  return 'After persisting this change, all pending answers will be removed. Deletion is irreversible.';
};

export const confirmSurveyChangeCausingDeletionOfPendingAnswers = async (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  const warningBlockMessage = getPendingAnswersWarningBlockMessage(
    pendingAnswersInvalidationState
  );
  return confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.deletePendingAnswersTitle',
      'Delete pending answers'
    ),
    modalSize: ModalSize.S,
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.deletePendingAnswersConfirmButton',
      'Confirm'
    ),
    isConfirmButtonDanger: true,
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.deletePendingAnswersCancelButton',
      'Cancel'
    ),
    subtitle: t(
      i18nLocale,
      'surveyAdmin:modals.deletePendingAnswersSubtitle',
      'Do you wish to continue?'
    ),
    text: t(
      i18nLocale,
      'surveyAdmin:modals.deletePendingAnswersText',
      'Before making this change, we suggest going through any pending answers for this survey and resolving them.'
    ),
    warningBlockMessage,
  });
};

export const confirmQuestionDeletionModal = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.deleteQuestionTitle',
      'Delete this question'
    ),
    modalSize: ModalSize.XS,
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.deleteQuestionConfirmButton',
      'Delete'
    ),
    isConfirmButtonDanger: true,
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.deleteQuestionCancelButton',
      'Cancel'
    ),
    subtitle: t(
      i18nLocale,
      'surveyAdmin:modals.deleteQuestionSubtitle',
      'Do you wish to continue?'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'surveyAdmin:modals.deleteQuestionWarning',
      'There are some extra questions depending on this field question. After deleting this question, your all conditional questions will be removed. Deletion is irreversible.'
    ),
  });
};

export const openResponseLogModal = async (
  surveyName: string,
  updateType: UpdateType
): Promise<boolean> => {
  return modal<boolean>(resolve => (
    <ResponseLogModalBase
      closeResponseLogModal={() => {
        resolve(false);
      }}
      onComponentClick={() => {
        resolve(true);
      }}
      surveyName={surveyName}
      initialUpdateType={updateType}
    />
  ));
};

const getCommonPendingApprovalModalProps = (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
) => {
  if (
    pendingAnswersInvalidationState.action ===
    PendingAnswersActionForEditedSurvey.WILL_DELETE
  ) {
    const numPendingAnswers = pendingAnswersInvalidationState.numPendingAnswers;
    return {
      count: numPendingAnswers,
      warningBlockMessage:
        numPendingAnswers && numPendingAnswers > 0
          ? `After unpublishing this survey, ${numPendingAnswers} pending answer${numPendingAnswers === 1 ? '' : 's'} will be removed. Deletion is irreversible.`
          : 'After unpublishing this survey, all pending answers will be removed. Deletion is irreversible.',
      isConfirmButtonDanger: true,
    };
  }
  if (
    pendingAnswersInvalidationState.action ===
    PendingAnswersActionForEditedSurvey.FAILED_TO_FETCH
  ) {
    return {
      warningBlockMessage:
        'Could not confirm whether there are pending answers. If any exist, they will be removed. Deletion is irreversible.',
      isConfirmButtonDanger: true,
    };
  }
  return {};
};

export const confirmUnpublishModal = async (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    modalSize: ModalSize.S,
    title: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishTitle',
      "You're about to unpublish this survey"
    ),
    text: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishText',
      'Until publishing again, this survey will be visible only to admins.'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishConfirmButton',
      'Unpublish survey'
    ),
    ...getCommonPendingApprovalModalProps(pendingAnswersInvalidationState),
  });
};

export const confirmUnpublishDueToErrorModal = async (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    modalSize: ModalSize.S,
    title: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishDueToErrorTitle',
      'This survey must be unpublished due to errors'
    ),
    text: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishDueToErrorText',
      'The survey will only be visible to admins until the errors are fixed and the survey is re-published. Are you sure you would like to save these changes and unpublish this survey?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishConfirmButton',
      'Unpublish survey'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishDueToErrorCancelButton',
      'Cancel Save'
    ),
    ...getCommonPendingApprovalModalProps(pendingAnswersInvalidationState),
  });
};

export const confirmUnpublishWhenBroadcastsModal = async (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    modalSize: ModalSize.S,
    title: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishTitle',
      "You're about to unpublish this survey"
    ),
    text: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishWhenBroadcastsText',
      'You have one or more broadcasts running against this survey. If you unpublish the survey it will only be visible to admins and your broadcasts will be stopped. Are you sure you would like to unpublish this survey?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishConfirmButton',
      'Unpublish survey'
    ),
    ...getCommonPendingApprovalModalProps(pendingAnswersInvalidationState),
  });
};

export const confirmUnpublishDueToErrorWhenBroadcastsModal = async (
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirm({
    modalSize: ModalSize.S,
    title: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishDueToErrorTitle',
      'This survey must be unpublished due to errors'
    ),
    text: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishDueToErrorWhenBroadcastsText',
      'You have one or more broadcasts running against this survey. The survey will only be visible to admins and your broadcasts will be stopped. This will remain the case until the errors are fixed and the survey is re-published. Are you sure you would like to save these changes and unpublish this survey?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishConfirmButton',
      'Unpublish survey'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.unpublishDueToErrorCancelButton',
      'Cancel Save'
    ),
    ...getCommonPendingApprovalModalProps(pendingAnswersInvalidationState),
  });
};

export const shouldDiscardChanges = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return await confirm({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.discardChangesTitle',
      'Discard unsaved changes'
    ),
    subtitle: t(
      i18nLocale,
      'surveyAdmin:modals.discardChangesSubtitle',
      'You have unsaved changes that will be lost. Are you sure you want to proceed?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'surveyAdmin:modals.discardChangesConfirmButton',
      'Yes, discard my changes'
    ),
  });
};

export const showMainApproverDialog = async (surveyName: string) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders info() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return info({
    title: t(
      i18nLocale,
      'surveyAdmin:modals.approverTitle',
      'You are now an approver'
    ),
    modalSize: ModalSize.S,
    primaryButtonText: t(
      i18nLocale,
      'surveyAdmin:modals.approverPrimaryButton',
      'Okay'
    ),
    text: (
      <Trans
        i18nKey="surveyAdmin:modals.approverMessage"
        defaults="You have been made an approver for <strong>{{surveyName}} survey.</strong> This means you will be notified when there are new responses to the survey for you to approve or reject."
        values={{ surveyName }}
        components={{ strong: <strong /> }}
      />
    ),
  });
};
